import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Phone, Mail, MessageSquare, MapPin, Calendar, FileText, Plus, Building2, User, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { useClientStore } from '@/store/clientStore';
import { formatDate, formatDateTime } from '@/utils/date';
import { toast } from 'sonner';
import NewClientModal from '@/components/clients/NewClientModal';
import { useAuthStore } from '@/store/authStore';
import { BillingClientPanel } from '@/pages/billing/BillingClientPanel';

export default function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [showEditModal, setShowEditModal] = useState(false);
  const [contactsWriteEnabled, setContactsWriteEnabled] = useState(false);
  const [publishingGoogle, setPublishingGoogle] = useState(false);
  const { 
    selectedClient: client, 
    isLoading, 
    error, 
    getClient, 
    clearError 
  } = useClientStore();

  useEffect(() => {
    if (id) {
      getClient(id).catch(() => {
        toast.error('Errore nel caricamento cliente');
      });
    }
  }, [id, getClient]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      clearError();
    }
  }, [error, clearError]);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    fetch('/api/gmail/oauth/status', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.success) {
          setContactsWriteEnabled(Boolean(data.data?.contactsWriteEnabled));
        }
      })
      .catch(() => {});
  }, []);

  const handlePublishToGoogle = async () => {
    if (!id) return;
    setPublishingGoogle(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`/api/gmail/contacts/publish-from-client/${id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Cliente pubblicato su Google Contatti');
        void getClient(id);
      } else {
        toast.error(data.message || 'Pubblicazione fallita');
      }
    } catch {
      toast.error('Pubblicazione fallita');
    } finally {
      setPublishingGoogle(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Link to="/clients">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Torna ai clienti
            </Button>
          </Link>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Link to="/clients">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Torna ai clienti
            </Button>
          </Link>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900">Cliente non trovato</h3>
              <p className="mt-1 text-sm text-gray-500">
                Il cliente richiesto non esiste o non hai i permessi per visualizzarlo.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/clients">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Torna ai clienti
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {client.firstName} {client.lastName}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Cliente dal {formatDate(client.createdAt)}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {contactsWriteEnabled && (
            <Button variant="outline" onClick={handlePublishToGoogle} disabled={publishingGoogle}>
              <Upload className="h-4 w-4 mr-2" />
              {publishingGoogle ? 'Pubblicazione…' : 'Pubblica su Google'}
            </Button>
          )}
          <Button onClick={() => setShowEditModal(true)}>
            <Edit className="h-4 w-4 mr-2" />
            Modifica
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Client Info */}
        <div className="lg:col-span-1 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Informazioni Personali</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-center">
                <div className="h-20 w-20 rounded-full bg-blue-600 flex items-center justify-center">
                  <span className="text-2xl font-medium text-white">
                    {client.firstName[0]}{client.lastName[0]}
                  </span>
                </div>
              </div>
              
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900">
                  {client.firstName} {client.lastName}
                </h3>
                <p className="text-sm text-gray-500">
                  {client.birthDate ? `Nato il ${formatDate(client.birthDate)}` : 'Data di nascita non disponibile'}
                  {client.birthPlace && ` a ${client.birthPlace}`}
                </p>
              </div>

              {/* Contatti */}
              <div className="border-t pt-4 space-y-3">
                <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
                  <Phone className="h-4 w-4" /> Contatti
                </h4>
                <div className="flex items-center space-x-3">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-900">{client.email || 'Email non disponibile'}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-900">{client.phone || 'Telefono non disponibile'}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <MessageSquare className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-900">{client.whatsappNumber || 'WhatsApp non disponibile'}</span>
                </div>
              </div>

              {/* Residenza */}
              <div className="border-t pt-4 space-y-3">
                <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
                  <MapPin className="h-4 w-4" /> Residenza
                </h4>
                <div className="text-sm text-gray-900">
                  <div>{client.address || 'Indirizzo non disponibile'}</div>
                  <div>
                    {client.city || 'Città'} {client.province && `(${client.province})`}
                    {client.postalCode && ` - ${client.postalCode}`}
                  </div>
                  {client.country && (
                    <div className="text-xs text-gray-500 mt-1">Paese: {client.country}</div>
                  )}
                </div>
              </div>

              {/* Dati Fiscali */}
              <div className="border-t pt-4 space-y-3">
                <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
                  <Building2 className="h-4 w-4" /> Dati Fiscali
                </h4>
                <div>
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Codice Fiscale
                  </span>
                  <p className="text-sm text-gray-900">{client.fiscalCode || 'Non disponibile'}</p>
                </div>
                <div>
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Partita IVA
                  </span>
                  <p className="text-sm text-gray-900">{client.vatNumber || 'Non disponibile'}</p>
                </div>
              </div>

              {/* Note */}
              <div className="border-t pt-4">
                <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2 mb-2">
                  <FileText className="h-4 w-4" /> Note
                </h4>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">
                  {client.notes || 'Nessuna nota disponibile'}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card>
            <CardHeader>
              <CardTitle>Statistiche</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {client._count?.practices || 0}
                  </div>
                  <div className="text-xs text-gray-500">Pratiche Totali</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {client.practices?.filter(p => p.status === 'COMPLETED' || p.status === 'ARCHIVED').length || 0}
                  </div>
                  <div className="text-xs text-gray-500">Pratiche Chiuse</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-600">
                    {client.practices?.filter(p => p.status === 'PENDING' || p.status === 'IN_PROGRESS').length || 0}
                  </div>
                  <div className="text-xs text-gray-500">Pratiche Aperte</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">
                    {client.documentsFromFoldersTotal ?? 0}
                  </div>
                  <div className="text-xs text-gray-500">Documenti (cartelle NAS)</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {client._count?.whatsappChats || 0}
                  </div>
                  <div className="text-xs text-gray-500">WhatsApp</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {user?.canAccessQuotesAndAccounts && id && <BillingClientPanel clientId={id} />}
          {/* Practices */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Pratiche</CardTitle>
              {client.practices && client.practices.length > 0 && (
                <Button size="sm" onClick={() => navigate(`/practices/new?clientId=${client.id}`)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Nuova Pratica
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {client.practices && client.practices.length > 0 ? (
                <div className="space-y-4">
                  {client.practices.slice(0, 5).map((practice) => (
                    <Link
                      key={practice.id}
                      to={`/practices/${practice.id}`}
                      className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <FileText className="h-5 w-5 text-gray-400" />
                        <div>
                          <p className="font-medium text-gray-900">
                            {practice.practiceNumber ? (
                              <span className="text-muted-foreground font-normal mr-1">#{practice.practiceNumber}</span>
                            ) : null}
                            {practice.title}
                          </p>
                          <p className="text-sm text-gray-500">
                            {typeof practice.type === 'object' && practice.type && 'name' in practice.type
                              ? (practice.type as { name: string }).name
                              : String(practice.type ?? '')}{' '}
                            • {formatDate(practice.startDate)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          practice.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                          practice.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-800' :
                          practice.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {practice.status === 'COMPLETED' ? 'Completata' :
                           practice.status === 'IN_PROGRESS' ? 'In Corso' :
                           practice.status === 'PENDING' ? 'In Attesa' :
                           practice.status}
                        </span>
                        {user?.canAccessQuotesAndAccounts && practice.amount && (
                          <span className="text-sm font-medium text-gray-900">
                            €{practice.amount}
                          </span>
                        )}
                      </div>
                    </Link>
                  ))}
                  {client.practices.length > 5 && (
                    <div className="text-center">
                      <Button variant="outline" size="sm">
                        Vedi tutte le pratiche ({client.practices.length})
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Nessuna pratica</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Inizia creando la prima pratica per questo cliente.
                  </p>
                  <div className="mt-6">
                    <Button size="sm" onClick={() => navigate(`/practices/new?clientId=${client.id}`)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Nuova Pratica
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Activities */}
          <Card>
            <CardHeader>
              <CardTitle>Attività Recenti</CardTitle>
            </CardHeader>
            <CardContent>
              {client.practices && client.practices.some(p => p.activities && p.activities.length > 0) ? (
                <div className="space-y-4">
                  {client.practices
                    .flatMap(practice => practice.activities || [])
                    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                    .slice(0, 10)
                    .map((activity) => (
                      <Link
                        key={activity.id}
                        to={`/activities/${activity.id}`}
                        className="flex items-center space-x-3 hover:bg-gray-50 p-2 rounded-lg cursor-pointer transition-colors"
                      >
                        <div className={`flex-shrink-0 w-2 h-2 rounded-full ${
                          activity.status === 'COMPLETED' ? 'bg-green-400' :
                          activity.status === 'IN_PROGRESS' ? 'bg-blue-400' :
                          'bg-yellow-400'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">
                            {activity.title}
                          </p>
                          <p className="text-sm text-gray-500">
                            {formatDateTime(activity.createdAt)}
                            {activity.user && (
                              <span className="ml-2">
                                • {activity.user.firstName} {activity.user.lastName}
                              </span>
                            )}
                          </p>
                        </div>
                      </Link>
                    ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Calendar className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Nessuna attività</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Le attività future appariranno qui.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Modal Modifica */}
      {client && (
        <NewClientModal 
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            if (id) getClient(id);
          }}
          editClient={{
            id: client.id,
            firstName: client.firstName,
            lastName: client.lastName,
            email: client.email,
            phone: client.phone,
            whatsappNumber: client.whatsappNumber,
            fiscalCode: client.fiscalCode,
            vatNumber: client.vatNumber,
            address: client.address,
            city: client.city,
            province: client.province,
            postalCode: client.postalCode,
            country: client.country,
            birthDate: client.birthDate,
            birthPlace: client.birthPlace,
            notes: client.notes,
          }}
        />
      )}
    </div>
  );
}
